import { realtimeMessageSchema, type RealtimeCommand } from '@contracts/realtime'
import { TopicRegistry } from './topic-registry'
import type { ConnectionState, RealtimeClient, RealtimeHandler, Unsubscribe } from './types'

type WebSocketFactory = (url: string) => WebSocket

/**
 * Minimal WebSocket client (foundation). Deliberately NOT included yet: reconnect, backoff,
 * heartbeat (→ `degraded`), batching. Those arrive with the realtime/device-monitoring changes.
 */
export class WsRealtimeClient implements RealtimeClient {
  private readonly registry = new TopicRegistry()
  private readonly createSocket: WebSocketFactory
  private socket: WebSocket | null = null
  private current: ConnectionState = 'idle'

  constructor(
    private readonly url: string,
    createSocket?: WebSocketFactory,
  ) {
    this.createSocket = createSocket ?? ((target) => new WebSocket(target))
  }

  get state(): ConnectionState {
    return this.current
  }

  connect(): void {
    if (this.socket) return
    this.setState('connecting')
    let socket: WebSocket
    try {
      socket = this.createSocket(this.url)
    } catch {
      this.setState('offline')
      return
    }
    this.socket = socket
    socket.addEventListener('open', () => {
      if (this.socket !== socket) return
      this.setState('online')
      for (const topic of this.registry.topics()) this.send({ action: 'subscribe', topic })
    })
    socket.addEventListener('close', () => {
      if (this.socket !== socket) return
      this.socket = null
      this.setState('offline')
    })
    socket.addEventListener('message', (event: MessageEvent) => {
      if (this.socket !== socket || typeof event.data !== 'string') return
      this.handleMessage(event.data)
    })
  }

  disconnect(): void {
    const socket = this.socket
    this.socket = null
    socket?.close()
    this.setState('idle')
  }

  onStateChange(listener: (state: ConnectionState) => void): Unsubscribe {
    return this.registry.onState(listener)
  }

  subscribe<T = unknown>(topic: string, handler: RealtimeHandler<T>): Unsubscribe {
    const h = handler as RealtimeHandler
    if (this.registry.add(topic, h)) this.send({ action: 'subscribe', topic })
    return () => {
      if (this.registry.remove(topic, h)) this.send({ action: 'unsubscribe', topic })
    }
  }

  private handleMessage(data: string): void {
    let raw: unknown
    try {
      raw = JSON.parse(data)
    } catch {
      return
    }
    const parsed = realtimeMessageSchema.safeParse(raw)
    if (!parsed.success) {
      if (import.meta.env.DEV)
        console.warn('[realtime] dropped message violating contract', parsed.error.issues)
      return
    }
    this.registry.dispatch(parsed.data)
  }

  private send(command: RealtimeCommand): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(command))
  }

  private setState(state: ConnectionState): void {
    if (state === this.current) return
    this.current = state
    this.registry.emitState(state)
  }
}

/** Resolves `/ws` against the page origin (http→ws, https→wss); absolute ws(s) URLs pass through. */
export function resolveWsUrl(
  wsBaseUrl: string,
  location: Pick<Location, 'protocol' | 'host'>,
): string {
  if (/^wss?:\/\//i.test(wsBaseUrl)) return wsBaseUrl
  if (/^https?:\/\//i.test(wsBaseUrl)) return wsBaseUrl.replace(/^http/i, 'ws')
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  const path = wsBaseUrl.startsWith('/') ? wsBaseUrl : `/${wsBaseUrl}`
  return `${protocol}//${location.host}${path}`
}
